import { CartesianGrid, Label, Legend, Line, LineChart, XAxis, YAxis } from 'recharts';
import type {
  CustomerAnalyticsMoodysReportDto,
  CustomerAnalyticsMoodysScenarioDetailsDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { renderChartToPdf } from './pdfImageUtils';
import { PureComponent } from 'react';

export type Scenario = 'RENT' | 'VACANCY' | 'INVENTORY';
interface ScenarioChartProps {
  report: CustomerAnalyticsMoodysReportDto;
  scenario: Scenario;
}

const ScenarioLineChartBuilder = ({ report, scenario }: ScenarioChartProps) => {
  const currentYear = new Date().getFullYear();
  const maxYearsInPast = 0;
  const maxYearsInFuture = 10;

  const colorMap = { market: '#e82b27', submarket: '#006AFF', nation: '#128A29', s1: '#e82b27', s2: '#F2A619' };

  const getLabel = () =>
    scenario === 'RENT'
      ? 'Asking Rent (% Change)'
      : scenario === 'INVENTORY'
        ? 'Inventory (% Change)'
        : 'Vacancy (% Change)';

  const yearFilter = (item: CustomerAnalyticsMoodysScenarioDetailsDto) =>
    currentYear - maxYearsInPast <= item.year && item.year <= currentYear + maxYearsInFuture;

  const submarketData = report.scenarioTrendsReport?.submarket;
  const marketData = report.scenarioTrendsReport.market;

  const hasSubmarketData = submarketData.length > 0;

  const getItemData = (item: CustomerAnalyticsMoodysScenarioDetailsDto | undefined) =>
    item &&
    (scenario === 'RENT'
      ? { value: item.askingRentChange, s1: item.askingRentChangeS1, s2: item.askingRentChangeS2 }
      : scenario === 'INVENTORY'
        ? { value: item.inventoryChange, s1: item.inventoryChangeS1, s2: item.inventoryChangeS2 }
        : { value: item.vacancyPercent, s1: item.vacancyPercentS1, s2: item.vacancyPercentS2 });

  const data = marketData
    .filter(yearFilter)
    .sort((a, b) => a.year - b.year)
    .map((item) => {
      const itemData = getItemData(item);
      if (!itemData) return;

      const subItemData = getItemData(submarketData?.find((item2) => item2.year === item.year));
      const nationData = getItemData(report.scenarioTrendsReport.national?.find((item2) => item2.year === item.year));
      return {
        name: item.year,
        market: itemData.value,
        submarket: subItemData?.value,
        s1: subItemData?.s1,
        s2: subItemData?.s2,
        nation: nationData?.value,
      };
    });

  class DataLabel extends PureComponent {
    render() {
      // @ts-ignore
      // eslint-disable-next-line react/prop-types
      const { x, y, stroke, value } = this.props;

      const shrunkated = (Number(value) * 100).toPrecision(2);

      return (
        <text x={x} y={y} dy={-12} fill={stroke} color={stroke} fontSize={15} textAnchor="middle">
          {shrunkated}%
        </text>
      );
    }
  }

  return (
    <LineChart
      width={1400}
      height={700}
      data={data}
      margin={{
        top: 20,
        right: 20,
        left: 20,
        bottom: 30,
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis
        domain={['auto', 'auto']}
        padding={{ top: 25, bottom: 20 }}
        tickFormatter={(value: number) => (value * 100).toFixed(2)}
      >
        <Label
          value={getLabel()}
          offset={-8}
          angle={-90}
          position="insideLeft"
          style={{ textAnchor: 'middle', fontSize: '15px' }}
        />
      </YAxis>
      <Legend wrapperStyle={{ position: 'relative', fontSize: '15px', marginTop: '-15px' }} iconType="circle" />
      {hasSubmarketData && (
        <Line
          isAnimationActive={false}
          dataKey="submarket"
          type="monotone"
          stroke={colorMap.submarket}
          dot={{ fill: colorMap.submarket }}
          name={report.submarketName}
          // @ts-ignore
          label={<DataLabel stroke={colorMap.submarket} />}
          strokeWidth={3}
        />
      )}
      {hasSubmarketData && (
        <Line
          isAnimationActive={false}
          dataKey="s1"
          stroke={colorMap.s1}
          type="monotone"
          strokeDasharray="2 2"
          name="S1 - % Change1"
          strokeWidth={3}
        />
      )}
      {hasSubmarketData && (
        <Line
          isAnimationActive={false}
          type="monotone"
          dataKey="s2"
          stroke={colorMap.s2}
          strokeDasharray="5 5"
          name="S2 - % Change2"
          strokeWidth={3}
        />
      )}
      <Line
        isAnimationActive={false}
        type="monotone"
        dataKey="market"
        stroke={colorMap.market}
        dot={{ fill: colorMap.market }}
        // @ts-ignore
        label={<DataLabel stroke={colorMap.market} />}
        name={report.marketName}
        strokeWidth={3}
      />
      <Line
        isAnimationActive={false}
        type="monotone"
        dataKey="nation"
        stroke={colorMap.nation}
        dot={false}
        name="United States"
        strokeWidth={3}
      />
    </LineChart>
  );
};

export const renderScenarioAskingRentLineChart = async (report: CustomerAnalyticsMoodysReportDto): Promise<string> => {
  return await renderChartToPdf(<ScenarioLineChartBuilder scenario="RENT" report={report} />);
};

export const renderScenarioVacancyLineChart = async (report: CustomerAnalyticsMoodysReportDto): Promise<string> => {
  return await renderChartToPdf(<ScenarioLineChartBuilder scenario="VACANCY" report={report} />);
};

export const renderScenarioInventoryLineChart = async (report: CustomerAnalyticsMoodysReportDto): Promise<string> => {
  return await renderChartToPdf(<ScenarioLineChartBuilder scenario="INVENTORY" report={report} />);
};
